import { parseISO } from "date-fns";
import { HistoryGroupBy } from "@common/constants/history-source-types";
import { APIProcessName } from "@common/constants/process-names";
import { ActorType, ResourceType } from "@common/types/auditLog";
import type {
    CoaTemplateApplyHistoryEventRef,
    HistoryItem,
} from "@common/types/auditLog";
import type { ChartOfAccountsTemplateApplyEventDetail } from "@common/schemas/domains/accounting/chart-of-accounts-template-apply-event.schema";
import type { CoaTemplateActivityEventResult } from "./coaTemplateActivityEvents";
import type { HistoryRow } from "../../views/historyViewConfig";

type LoadedEvent = Extract<
    CoaTemplateActivityEventResult,
    { status: "loaded" }
>;

function isLoaded(event: CoaTemplateActivityEventResult): event is LoadedEvent {
    return event.status === "loaded";
}

// Entities of the currently-loaded events that have already been reverted. Used
// by the rail to swap a broken-looking disabled checkbox for a completed state.
export function getRevertedEntityIds(
    events: CoaTemplateActivityEventResult[],
): Set<number> {
    const entityIds = new Set<number>();
    for (const event of events) {
        if (isLoaded(event) && event.detail.revertedAt !== null) {
            entityIds.add(event.eventRef.entityId);
        }
    }
    return entityIds;
}

// Builds a synthetic Reverted-activity HistoryRow entirely from already-loaded
// details, scoped to the active detail's undo invocation. Returns null for
// legacy details with no invocation id or when the invocation cannot be
// reconstructed, so the caller hides the cross-link. Never sets revertedAt, so
// the resulting Reverted drawer does not recurse into another banner.
export function buildRevertedActivityRow({
    activeDetail,
    events,
}: {
    activeDetail: ChartOfAccountsTemplateApplyEventDetail;
    events: CoaTemplateActivityEventResult[];
}): HistoryRow | null {
    const { undoInvocationId, revertedAt } = activeDetail;
    if (!undoInvocationId || !revertedAt) {
        return null;
    }
    const matchingEvents = events.filter(
        (event): event is LoadedEvent =>
            isLoaded(event) &&
            event.detail.undoInvocationId === undoInvocationId,
    );
    if (matchingEvents.length === 0) {
        return null;
    }

    const eventRefs: CoaTemplateApplyHistoryEventRef[] = matchingEvents.map(
        (event) => event.eventRef,
    );
    const resourceIds = eventRefs.map((ref) => ref.eventId);
    const entityIds = eventRefs.map((ref) => ref.entityId);
    const date = parseISO(revertedAt);
    const actorId = activeDetail.revertedByUserId;
    const actorName = activeDetail.revertedByUserName;
    const actorType = actorId === null ? ActorType.SYSTEM : ActorType.USER;

    const historyItem: HistoryItem = {
        date,
        sourceType: APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY_UNDO,
        resourceType: ResourceType.chartOfAccountsTemplateApplyEvent,
        actorId,
        actorName,
        actorType,
        fields: [],
        count: eventRefs.length,
        changeTypes: [],
        resourceIds,
        entityIds,
        sourceIds: [undoInvocationId],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        presentation: {
            kind: "coaTemplateApply",
            templateName: activeDetail.templateName,
            eventRefs,
        },
    };

    return {
        id: `${APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY_UNDO}-${undoInvocationId}`,
        date,
        actorId,
        actorName,
        actorType,
        sourceType: APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY_UNDO,
        resourceType: ResourceType.chartOfAccountsTemplateApplyEvent,
        changeTypes: [],
        fields: [],
        count: eventRefs.length,
        resourceIds,
        entityIds,
        historyItem,
        system: null,
        channel: null,
    };
}
